import {
  BlotterTableFormattedNumberProvider,
  Box,
  CopyButton,
  Divider,
  EMPTY_ARRAY,
  HStack,
  Icon,
  IconName,
  InlineFormattedNumber,
  NotificationVariants,
  NumberVariants,
  ProductTypeEnum,
  Side,
  Table,
  TableSize,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  VStack,
  WarningSeverity,
  WarningSeverityIcon,
  getLegFilledQtyAndNotional,
  getOrderIncrements,
  isMultileg,
  legSummaryHasUnhedgedAmt,
  setAlpha,
  toBigWithDefault,
  useCurrenciesContext,
  useIsMobile,
  useMarketAccountsContext,
  useMarketsContext,
  useSecuritiesContext,
  type AmountObject,
  type BoxProps,
  type IMultilegParameters12001LegParams,
  type IOMSExecutionReport4203LegSummary,
  type Order,
  type RequiredProperties,
  type Security,
} from '@talos/kyoko';
import Big from 'big.js';
import { compact } from 'lodash-es';
import styled from 'styled-components';

import { getLegMarketAccountsFromOrder } from '@src/components/OMS/NewOrder/utils';
import { useCallback, useMemo, type ReactNode } from 'react';

export type MultilegTableProps = {
  order: RequiredProperties<Order, 'legSummaryLegs' | 'legSummaryParent'>;
  orderSecurity: Security;
} & BoxProps;

export const MultilegTable = ({ order, orderSecurity, ...boxProps }: MultilegTableProps) => {
  const { securitiesBySymbol } = useSecuritiesContext();
  const { currenciesBySymbol } = useCurrenciesContext();
  const isMobile = useIsMobile();

  const multilegDetails = isMultileg(orderSecurity) ? orderSecurity.MultilegDetails : undefined;
  const legs = (multilegDetails?.Legs ?? orderSecurity.Legs) || EMPTY_ARRAY;
  const securityByLegIndex: (Security | undefined)[] = useMemo(
    () => legs.map(leg => securitiesBySymbol.get(leg.Symbol)),
    [legs, securitiesBySymbol]
  );

  const legSummaryLegs = useMemo(() => order.legSummaryLegs, [order.legSummaryLegs]);

  const nativeLegSummaries = useMemo(
    () =>
      order.legSummaryParent.filter(
        leg => securitiesBySymbol.get(leg.Symbol)?.ProductType !== ProductTypeEnum.Synthetic
      ),
    [order.legSummaryParent, securitiesBySymbol]
  );

  const mlLegSummaries = useMemo(
    () =>
      order.legSummaryParent.filter(
        leg => securitiesBySymbol.get(leg.Symbol)?.ProductType === ProductTypeEnum.Synthetic
      ),
    [order.legSummaryParent, securitiesBySymbol]
  );

  const { legMarketAccounts: legMarketAccountsByLegIndex } = useMemo(
    () => getLegMarketAccountsFromOrder(order, orderSecurity),
    [order, orderSecurity]
  );

  const isOrderLegged = !!order.RiskStatus?.Legged;
  // If this is true, it would be regarded as a bug since multiple legs shouldn't be unhedged at the same time.
  const isMultipleUnhedged = useMemo(
    () => legSummaryLegs.filter(leg => leg.UnhedgedAmt && !Big(leg.UnhedgedAmt).eq(0)).length > 1,
    [legSummaryLegs]
  );

  const createRow = useCallback(
    (leg: IOMSExecutionReport4203LegSummary, isLeg: boolean) => {
      // If multiple legs are unhedged, show only first leg.
      const showUnhedged = Boolean(
        isOrderLegged && leg.UnhedgedAmt && !Big(leg.UnhedgedAmt).eq(0) && (!isMultipleUnhedged || leg.LegIndex === 0)
      );

      const rowSecurity = isLeg ? securityByLegIndex[leg.LegIndex] : securitiesBySymbol.get(leg.Symbol);
      const currentLegDetails = legs.at(leg.LegIndex);
      const filledPercent = (
        toBigWithDefault(order.OrderQty, 0).gt(0) ? toBigWithDefault(leg.ParentCumQty, 0).div(order.OrderQty) : Big(0)
      )
        .times(100)
        .toNumber()
        .toFixed(2);

      const Symbol = rowSecurity?.DisplaySymbol || currentLegDetails?.Symbol;
      if (!Symbol) {
        return null;
      }

      const { filledQty, filledNotional } = getLegFilledQtyAndNotional(leg, rowSecurity);

      const orderIncrements = getOrderIncrements(
        { Currency: filledQty.currency },
        rowSecurity,
        filledQty.currency ? currenciesBySymbol?.get(filledQty.currency) : undefined
      );

      const legMarketAccounts = isLeg ? legMarketAccountsByLegIndex.at(leg.LegIndex) : undefined;
      const legParamsData = isLeg ? order.MultilegParams?.LegParams?.at(leg.LegIndex) : undefined;

      return {
        Symbol,
        legSecurity: rowSecurity,
        filledQty,
        filledNotional,
        filledPercent,
        showUnhedged,
        leg,
        orderIncrements,
        isNative: rowSecurity?.ProductType !== ProductTypeEnum.Synthetic && !isLeg,
        isParent: rowSecurity?.ProductType === ProductTypeEnum.Synthetic && !isLeg,
        legMarketAccounts,
        legParamsData,
      };
    },
    [
      isOrderLegged,
      isMultipleUnhedged,
      securityByLegIndex,
      securitiesBySymbol,
      legs,
      order.OrderQty,
      order.MultilegParams?.LegParams,
      currenciesBySymbol,
      legMarketAccountsByLegIndex,
    ]
  );

  const rows = useMemo(() => {
    return compact([
      ...mlLegSummaries.map(leg => createRow(leg, false)),
      ...legSummaryLegs.map(syntheticLeg => createRow(syntheticLeg, true)),
      ...(nativeLegSummaries ? nativeLegSummaries.map(leg => createRow(leg, false)) : []),
    ]);
  }, [createRow, legSummaryLegs, nativeLegSummaries, mlLegSummaries]);

  const renderRow = ({
    Symbol,
    filledQty,
    filledNotional,
    filledPercent,
    showUnhedged,
    leg,
    orderIncrements,
    legSecurity,
    isNative,
    isParent,
    legMarketAccounts,
    legParamsData,
  }: (typeof rows)[number]) => {
    if (isMobile) {
      return (
        <MobileLegCard key={leg.Symbol}>
          <HStack justifyContent="space-between" alignItems="flex-start" gap="spacing8">
            <HStack justifyContent="flex-start" gap="spacing4" minWidth={0}>
              {!(isNative || isParent) && <Icon icon={IconName.Connector} color="colorTextMuted" size={18} />}
              <Text color="colorTextImportant">
                <MultilegTableSymbolDisplay symbol={Symbol} />
              </Text>
            </HStack>
            <HStack gap="spacing8">
              <Side side={leg.Side}>{leg.Side}</Side>
              {legParamsData && (
                <LegParamsTooltip
                  legIndex={leg.LegIndex}
                  legMarketAccounts={legMarketAccounts}
                  legParams={legParamsData}
                />
              )}
            </HStack>
          </HStack>
          <Divider color="backgroundDivider" />
          <MobileLegMetrics>
            <MobileLegMetric label="Filled Qty">
              <InlineFormattedNumber
                pretty
                number={filledQty.value}
                currency={filledQty.currency}
                increment={orderIncrements.quantityIncrement}
                specification={legSecurity?.SizeDisplaySpec}
              />
            </MobileLegMetric>
            <MobileLegMetric label="Filled Price">
              <InlineFormattedNumber
                pretty
                number={leg.AvgPx || '0'}
                currency={legSecurity?.QuoteCurrency}
                increment={legSecurity?.DefaultPriceIncrement}
                specification={legSecurity?.PriceDisplaySpec}
              />
            </MobileLegMetric>
            <MobileLegMetric label="Filled Amt">
              {showUnhedged && legSummaryHasUnhedgedAmt(leg) ? (
                <FilledAmount filledNotional={filledNotional} legSecurity={legSecurity} leg={leg} />
              ) : (
                <InlineFormattedNumber
                  pretty
                  number={filledNotional.value}
                  currency={filledNotional.currency}
                  increment={legSecurity?.DefaultPriceIncrement}
                  specification={legSecurity?.PriceDisplaySpec}
                />
              )}
            </MobileLegMetric>
            <MobileLegMetric label="Filled %">
              <InlineFormattedNumber pretty round number={filledPercent} currency="%" />
            </MobileLegMetric>
          </MobileLegMetrics>
        </MobileLegCard>
      );
    }
    return (
      <Tr key={leg.Symbol} background={isNative || isParent ? 'transparent' : 'gray.030'}>
        <Td color="colorTextImportant" width="auto">
          <Box overflow="hidden" display="inline-flex" alignItems="center" gap="spacing4">
            {!(isNative || isParent) && <Icon icon={IconName.Connector} color="colorTextMuted" size={18} />}
            <span>
              <MultilegTableSymbolDisplay symbol={Symbol} />
            </span>
          </Box>
        </Td>
        <Td align="left">
          <Side side={leg.Side}>{leg.Side}</Side>
        </Td>
        <Td>
          <InlineFormattedNumber
            pretty
            number={filledQty.value}
            currency={filledQty.currency}
            increment={orderIncrements.quantityIncrement}
            specification={legSecurity?.SizeDisplaySpec}
          />
        </Td>
        <Td>
          <InlineFormattedNumber
            pretty
            number={leg.AvgPx || '0'}
            currency={legSecurity?.QuoteCurrency}
            increment={legSecurity?.DefaultPriceIncrement}
            specification={legSecurity?.PriceDisplaySpec}
          />
        </Td>
        <Td align="right">
          {showUnhedged && legSummaryHasUnhedgedAmt(leg) ? (
            <FilledAmount filledNotional={filledNotional} legSecurity={legSecurity} leg={leg} />
          ) : (
            <InlineFormattedNumber
              pretty
              number={filledNotional.value}
              currency={filledNotional.currency}
              increment={legSecurity?.DefaultPriceIncrement}
              specification={legSecurity?.PriceDisplaySpec}
            />
          )}
        </Td>
        <Td color="colorTextImportant" align="right">
          <InlineFormattedNumber pretty round number={filledPercent} currency="%" />
        </Td>
        <Td minimize>
          {legParamsData && (
            <LegParamsTooltip legIndex={leg.LegIndex} legMarketAccounts={legMarketAccounts} legParams={legParamsData} />
          )}
        </Td>
      </Tr>
    );
  };

  return (
    <BlotterTableFormattedNumberProvider>
      <TableWrapper {...boxProps}>
        {isMobile ? (
          <MobileLegList>{rows.map(renderRow)}</MobileLegList>
        ) : (
          <CustomTable insetPadding="spacing12" size={TableSize.Small}>
            <Thead>
              <Tr>
                <Th>Symbol</Th>
                <Th align="left">Side</Th>
                <Th>Filled Qty</Th>
                <Th>Filled Price</Th>
                <Th align="right">Filled Amt</Th>
                <Th align="right">Filled %</Th>
                <Th />
              </Tr>
            </Thead>
            <Tbody>{rows.map(renderRow)}</Tbody>
          </CustomTable>
        )}
      </TableWrapper>
    </BlotterTableFormattedNumberProvider>
  );
};

const MobileLegMetric = ({ label, children }: { label: string; children: ReactNode }) => {
  return (
    <VStack alignItems="flex-start" gap="spacing2" minWidth={0}>
      <Text size="fontSizeSmall" color="colorTextSubtle">
        {label}
      </Text>
      <Text color="colorTextImportant">{children}</Text>
    </VStack>
  );
};

const LegParamsTooltip = ({
  legIndex,
  legMarketAccounts,
  legParams,
}: {
  legIndex: number;
  legMarketAccounts?: string[];
  legParams: IMultilegParameters12001LegParams;
}) => {
  const { marketAccountsByName } = useMarketAccountsContext();
  const { marketsByName } = useMarketsContext();

  const marketsDisplay = useMemo(() => {
    if (!legMarketAccounts || legMarketAccounts.length === 0) {
      return 'All';
    }
    return legMarketAccounts
      .map(ma => marketAccountsByName.get(ma)?.DisplayName ?? marketsByName.get(ma)?.DisplayName ?? ma)
      .join(', ');
  }, [legMarketAccounts, marketAccountsByName, marketsByName]);

  const tooltipTestId = (param: string) => `leg-params-tooltip-${legIndex}-${param}`;

  return (
    <Tooltip
      usePortal
      tooltip={
        <Box data-testid={tooltipTestId('container')}>
          <VStack alignItems="stretch" gap="spacing4" p="spacing4">
            <HStack justifyContent="space-between" gap="spacing16">
              <Text color="colorTextMuted" whiteSpace="nowrap">
                Markets
              </Text>
              <Text data-testid={tooltipTestId('markets')} color="colorTextImportant" textAlign="right">
                {marketsDisplay}
              </Text>
            </HStack>
            <HStack justifyContent="space-between" gap="spacing16">
              <Text color="colorTextMuted" whiteSpace="nowrap">
                Initiating
              </Text>
              <Box
                data-testid={tooltipTestId('initiating')}
                data-initiating={String(legParams.Initiating)}
                display="inline-flex"
              >
                <Icon
                  icon={legParams.Initiating ? IconName.Check : IconName.Close}
                  color={legParams.Initiating ? 'green.lighten' : 'red.lighten'}
                  size={16}
                />
              </Box>
            </HStack>
            {legParams.HaircutRatio != null && (
              <HStack justifyContent="space-between" gap="spacing16">
                <Text color="colorTextMuted" whiteSpace="nowrap">
                  Haircut Ratio
                </Text>
                <Text color="colorTextImportant" data-testid={tooltipTestId('haircut-ratio')}>
                  {legParams.HaircutRatio}
                </Text>
              </HStack>
            )}
          </VStack>
        </Box>
      }
    >
      <Icon icon={IconName.InformationCircle} color="colorTextSubtle" size={16} />
    </Tooltip>
  );
};

const MultilegTableSymbolDisplay = ({ symbol }: { symbol: string }) => {
  if (symbol.length > 8) {
    return (
      <Tooltip
        tooltip={<Text color="colorTextImportant">{symbol}</Text>}
        targetStyle={{ whiteSpace: 'nowrap' }}
        usePortal
      >
        {symbol}
      </Tooltip>
    );
  }
  return <>{symbol}</>;
};

const CustomTable = styled(Table)`
  ${Tbody} > ${Tr} {
    border-top: 1px solid ${({ theme }) => setAlpha(0.12, theme.colors.gray[100])};
  }

  ${Th} {
    text-transform: none;
    font-size: ${({ theme }) => theme.fontSizeXs}rem;
  }

  ${Tbody} ${Td}, ${Thead} ${Th} {
    padding: ${({ theme }) => theme.spacing8}px;
  }
`;

const MobileLegList = styled(VStack)`
  align-items: stretch;
  gap: ${({ theme }) => theme.spacing8}px;
  background: ${({ theme }) => theme.backgroundDrawer};
`;

const MobileLegCard = styled(VStack)`
  align-items: stretch;
  gap: ${({ theme }) => theme.spacing8}px;
  padding: ${({ theme }) => theme.spacing12}px ${({ theme }) => theme.spacing16}px;
  background: ${({ theme }) => theme.backgroundCard};
`;

const MobileLegMetrics = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: ${({ theme }) => `${theme.spacing8}px ${theme.spacing12}px`};
`;

const FilledAmount = ({
  filledNotional,
  legSecurity,
  leg,
}: {
  filledNotional: AmountObject;
  legSecurity?: Security;
  leg: RequiredProperties<IOMSExecutionReport4203LegSummary, 'UnhedgedAmt'>;
}) => {
  const { UnhedgedAmt, UnhedgedAmtCurrency } = leg;

  return (
    <HStack justifyContent="flex-end" gap="spacing2">
      <Tooltip
        usePortal
        tooltip={
          <HStack justifyContent="space-between" gap="spacing4">
            <span>
              {'Unhedged Amount: '}
              <Text color="colorTextImportant">{`${UnhedgedAmt} ${UnhedgedAmtCurrency ?? ''}`}</Text>
            </span>
            <CopyButton
              value={UnhedgedAmt}
              toastProps={{ text: `Copied ${UnhedgedAmt}`, variant: NotificationVariants.Positive }}
            />
          </HStack>
        }
      >
        <WarningSeverityIcon severity={WarningSeverity.MEDIUM} />
      </Tooltip>
      <InlineFormattedNumber
        variant={NumberVariants.Warning}
        pretty
        number={filledNotional.value}
        currency={filledNotional.currency}
        increment={legSecurity?.DefaultPriceIncrement}
        specification={legSecurity?.PriceDisplaySpec}
      />
    </HStack>
  );
};

export const TableWrapper = styled(Box)`
  border-radius: ${({ theme }) => theme.borderRadiusDefault}px;
  background: ${({ theme }) => theme.backgroundCard};
  width: 100%;
`;
